import { Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../modal/modal.component';

@Component({
  selector: 'app-payment-receipt',
  standalone: true,
  imports: [CommonModule, ModalComponent],
  templateUrl: './payment-receipt.component.html',
})
export class PaymentReceiptComponent {
  @ViewChild('receipt_modal') receipt_modal: ModalComponent | undefined;

  @Input() receipt_data: any = null;

  open(data: any) {
    this.receipt_data = data;
    this.receipt_modal?.open();
  }

  close() {
    this.receipt_modal?.close();
  }

  get_display_date(val: any): string {
    return this.format_date(val);
  }

  amount_in_words(amount: number): string {
    if (!amount) return '';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convert = (n: number): string => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };

    const parts = amount.toString().split('.');
    const rupees = parseInt(parts[0]);
    const paise = parts[1] ? parseInt(parts[1].padEnd(2, '0').substring(0, 2)) : 0;

    let result = convert(rupees) + ' Rupees';
    if (paise > 0) result += ' and ' + convert(paise) + ' Paise';
    return result + ' Only';
  }

  private format_number(n: number): string {
    if (n == null) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private format_date(val: any): string {
    const d = (val && !isNaN(new Date(val).getTime())) ? new Date(val) : new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    let hh = d.getHours();
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12 || 12;
    return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, '0')}:${min} ${ampm}`;
  }

  print_receipt() {
    const d = this.receipt_data;
    if (!d) return;

    const logo_html = d.university?.logo_url
      ? `<img src="${d.university.logo_url}" style="width:65px;height:47px;object-fit:contain;display:block;margin:0 auto 4px;" alt="logo">`
      : '';

    const sig_html = d.university?.signature_url
      ? `<img src="${d.university.signature_url}" style="width:50px;height:48px;object-fit:contain;display:block;margin:0 auto 8px;" alt="signature">`
      : '';

    const gst_html = d.university?.gst_enabled && d.university?.gstn
      ? `<p style="font-size:12px;color:#555;">GST Number: ${d.university.gstn}</p>`
      : '<div></div>';

    const items_html = (d.items || []).map((item: any, i: number) => `
      <div style="display:flex;justify-content:space-between;gap:16px;">
        <span style="font-size:12px;color:#535353;"><span style="color:#575757;">${i + 1}.</span>&nbsp;&nbsp;${item.description}</span>
        <span style="font-size:12px;color:#585858;white-space:nowrap;">${this.format_number(item.amount)}</span>
      </div>`).join('<div style="height:12px;"></div>');

    const gateway = d.payment_gateway ? `, ${d.payment_gateway}` : '';

    /* each section is its own bordered box, sharp corners, matches Figma reference design */
    const sec = `border:1px solid #9e9e9e;padding:16px;margin-bottom:8px;`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Payment Receipt</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{
      font-family:Graphik,'Helvetica Neue',Arial,sans-serif;
      font-size:14px;color:#212121;background:#fff;
    }
    @page{margin:0;size:A4;}
    @media print{
      body{padding:24px;}
    }
    .row{display:flex;align-items:baseline;margin-bottom:16px;}
    .row:last-child{margin-bottom:0;}
    .lbl{font-weight:500;color:#666;min-width:100px;flex-shrink:0;}
    .val{color:#585858;}
  </style>
</head>
<body>
<div id="wrap" style="max-width:595px;margin:0 auto;display:flex;flex-direction:column;min-height:calc(297mm - 48px);">

  <!-- Header + Receipt Meta combined -->
  <div style="${sec}display:flex;flex-direction:column;align-items:flex-end;">
    ${d.academic_year ? `<div style="font-size:12px;white-space:nowrap;margin-bottom:16px;"><span style="font-weight:500;color:#3c3c3c;">Academic Year :</span> <span style="color:#525252;">${d.academic_year}</span></div>` : ''}
    <div style="width:100%;text-align:center;margin-bottom:16px;">
      ${logo_html}
      <div style="font-size:12px;font-weight:500;color:#3c3c3c;margin-bottom:4px;">${d.university?.name || ''}</div>
      <div style="font-size:10px;color:#5e5e5e;">${d.university?.address || ''}</div>
    </div>
    <div style="font-size:14px;font-weight:500;color:#212121;margin-bottom:16px;width:100%;text-align:center;">Digital Copy</div>
    <div style="width:100%;text-align:left;">
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:16px;gap:16px;">
        <div style="display:flex;align-items:baseline;">
          <span class="lbl">Receipt No</span>
          <span class="val">: ${d.receipt_no || '—'}</span>
        </div>
        <div style="display:flex;align-items:baseline;white-space:nowrap;">
          <span style="font-weight:500;color:#666;margin-right:0;">Date</span>
          <span class="val">&nbsp;: ${this.format_date(d.receipt_date)}</span>
        </div>
      </div>
      <div class="row">
        <span class="lbl">Received From</span>
        <span class="val">: ${d.student_name || '—'}</span>
      </div>
      <div class="row">
        <span class="lbl">Roll No.</span>
        <span class="val">: ${d.roll_number || '—'}</span>
      </div>
    </div>
  </div>

  <!-- Program & Exam -->
  <div style="${sec}">
    <div class="row">
      <span class="lbl">Program</span>
      <span class="val">: ${d.program || '—'}</span>
    </div>
    <div class="row">
      <span class="lbl">Exam</span>
      <span class="val">: ${d.exam || '—'}</span>
    </div>
  </div>

  <!-- Items + Total: flex:1 grows to fill remaining page height -->
  <div style="border:1px solid #9e9e9e;padding:8px 16px 16px;margin-bottom:8px;flex:1;display:flex;flex-direction:column;">
    <div style="flex:1;display:flex;flex-direction:column;gap:8px;">
      <div style="display:flex;justify-content:space-between;padding:8px 0;font-size:14px;font-weight:500;">
        <span style="color:#3d3d3d;">On Account Of</span>
        <span style="color:#383838;">Amount (&#8377;)</span>
      </div>
      <div>
        ${items_html}
      </div>
    </div>
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:12px;font-weight:500;color:#313131;">Receipt Total</span>
        <span style="font-size:20px;font-weight:500;color:#383838;">${this.format_number(d.total_amount)}</span>
      </div>
      <div style="display:flex;gap:2px;font-size:12px;margin-top:8px;">
        <span style="font-weight:500;color:#343434;min-width:102px;">&#8377; (in words)</span>
        <span style="color:#595959;">: ${this.amount_in_words(d.total_amount)} .</span>
      </div>
    </div>
  </div>

  <!-- Transaction & Payment -->
  <div style="border:1px solid #9e9e9e;padding:8px 16px 16px;margin-bottom:8px;font-size:12px;">
    <div style="margin-bottom:8px;">
      <span style="font-weight:500;color:#303030;">Transaction ID&nbsp;&nbsp;&nbsp;: </span>
      <span style="color:#595959;word-break:break-all;">${d.gateway_transaction_id || d.receipt_no || ''}${gateway}</span>
    </div>
    <div>
      <span style="font-weight:500;color:#303030;">Payment Method&nbsp;&nbsp;&nbsp;: </span>
      <span style="color:#595959;">${(d.payment_method || '').toUpperCase()}</span>
    </div>
  </div>

  <!-- Footer: GST + Signature outside boxes -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:8px 16px 0;">
    ${gst_html}
    <div style="text-align:center;">
      ${sig_html}
      <div style="font-size:12px;font-weight:500;color:#565656;white-space:nowrap;">${d.university?.signatory_name || ''}</div>
      <div style="font-size:12px;color:#a3a3a3;font-style:italic;white-space:nowrap;">${d.university?.designation || ''}</div>
    </div>
  </div>

</div>
</body>
</html>`;

    const print_window = window.open('', '_blank', 'width=820,height=950');
    if (!print_window) return;
    print_window.document.open();
    print_window.document.write(html);
    print_window.document.close();
    print_window.focus();
    setTimeout(() => { print_window.print(); }, 500);
  }
}
